<template>
  <f7-page data-tour-id="samples-register-template-create-page">
    <div
      v-if="templateCreationDraft"
      class="custom-table-container tableContainer"
      data-tour-id="samples-register-template-create-container"
    >
      <div class="summary-header">
        <div class="summary-title">
          <div class="summary-icon-templates"></div>
          <div class="summary-text">
            {{ pageTitleText }}
          </div>
        </div>
      </div>

      <div class="template-create-page">
        <div
          class="template-create-page__form-grid"
          data-tour-id="samples-register-template-create-form"
        >
          <div
            class="template-create-page__field"
            data-tour-id="samples-register-template-create-name-field"
          >
            <label class="template-create-page__label">
              {{ $t('templateName') }}
            </label>

            <DxTextBox
              :value="templateName"
              :input-attr="{ 'aria-label': $t('templateName') }"
              width="100%"
              @value-changed="onTemplateNameChanged"
            />

            <div
              v-if="showTemplateNameMaxLengthError"
              class="template-create-page__error"
            >
              {{ templateNameMaxLengthErrorText }}
            </div>
          </div>

          <div
            class="template-create-page__field template-create-description-field"
            data-tour-id="samples-register-template-create-description-field"
          >
            <label class="template-create-page__label">
              {{ $t('templateDescription') }}
            </label>

            <DxTextArea
              :value="templateDescription"
              :input-attr="{
                'aria-label': $t('templateDescription'),
              }"
              width="100%"
              @value-changed="onTemplateDescriptionChanged"
            />

            <div
              v-if="showTemplateDescriptionMaxLengthError"
              class="template-create-page__error"
            >
              {{ templateDescriptionMaxLengthErrorText }}
            </div>
          </div>

          <!-- <div class="template-create-page__field">
            <label class="template-create-page__label">
              {{ statusText }}
            </label>

            <DxSelectBox
              :items="templateStatusOptions"
              :value="templateActive"
              display-expr="label"
              value-expr="value"
              :input-attr="{ 'aria-label': statusText }"
              width="100%"
              @value-changed="onTemplateActiveChanged"
            />
          </div> -->

          <div
            v-if="showCommonBudgetField"
            class="template-create-page__field template-create-page__field--budget"
            data-tour-id="samples-register-template-create-budget-field"
          >
            <label class="template-create-page__label">
              {{ budgetText }}
            </label>

            <DxSelectBox
              :items="normalizedBudgetOptions"
              :value="templateBudgetCode"
              :display-expr="getBudgetOptionText"
              value-expr="CodPresupuesto"
              :search-enabled="true"
              :disabled="true"
              :input-attr="{ 'aria-label': budgetText }"
              width="100%"
              @value-changed="onTemplateBudgetChanged"
            />
          </div>
        </div>

        <div
          v-if="showTemplateNameError"
          class="template-create-page__error"
        >
          {{ $t('templateNameRequired') }}
        </div>

        <div class="template-create-page__preview">
          <TemplateSamplesDetails
            ref="templateSamplesDetails"
            :samples="templateSamples"
            :editable="true"
            :saving="savingTemplate"
            :budget-options="normalizedBudgetOptions"
            :budget-code="
              showCommonBudgetField ? templateBudgetCode : null
            "
            :show-budget-column="hasMultipleSampleBudgets"
            @samples-updated="onTemplateSamplesUpdated"
          />
        </div>

        <div class="template-create-page__actions">
          <f7-button
            class="btn-cancel"
            outline
            type="normal"
            @click="goBack"
          >
            {{ $t('cancel') }}
          </f7-button>

          <f7-button
            class="btn-accept"
            data-tour-id="samples-register-template-create-save-button"
            fill
            outline
            type="default"
            :disabled="
              savingTemplate ||
              !templateSamples.length ||
              !canSaveTemplateWithCurrentBudgets
            "
            @click="saveTemplate"
          >
            {{ $t('saveTemplate') }}
          </f7-button>
        </div>
      </div>
    </div>
  </f7-page>
</template>

<script>
import DxTextBox from 'devextreme-vue/text-box';
import DxTextArea from 'devextreme-vue/text-area';
import DxSelectBox from 'devextreme-vue/select-box';
import { mapActions, mapState } from 'vuex';
import {
  NOTIFY_TIME_LONG,
  SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY,
} from '@/js/constants';
import TemplateSamplesDetails from '@/components/TemplatesManager/TemplateSamplesDetails/index.vue';
import {
  consumePendingSamplesRegisterTemplateCreateTourStart,
  startSamplesRegisterTemplateCreateGuidedTour,
  stopSamplesRegisterGuidedTour,
} from '@/services/samplesRegisterGuidedTour';
import { emitSamplesRegisterGa4Event } from '@/services/samplesRegisterAnalytics';

const TEMPLATE_NAME_MAX_LENGTH = 120;
const TEMPLATE_DESCRIPTION_MAX_LENGTH = 250;

export default {
  name: 'TemplateCreatePage',
  components: {
    DxTextBox,
    DxTextArea,
    DxSelectBox,
    TemplateSamplesDetails,
  },
  data() {
    return {
      templateName: '',
      templateDescription: '',
      templateActive: true,
      templateBudgetCode: null,
      templateSamplesLocal: [],
      attemptedSubmit: false,
      savingTemplate: false,
      loaded: false,
      guidedTourStartTimeoutId: null,
    };
  },
  computed: {
    ...mapState('samplingTemplatesModule', ['templateCreationDraft']),
    ...mapState('Offers', ['presupuestos', 'detallesPresupuestos']),
    editingTemplateId() {
      return (
        this.templateCreationDraft?.editingTemplateId ||
        this.templateCreationDraft?.template?.id ||
        null
      );
    },
    isEditMode() {
      return Boolean(this.editingTemplateId);
    },
    pageTitleText() {
      if (this.isEditMode) {
        return this.$t('editTemplate') !== 'editTemplate'
          ? this.$t('editTemplate')
          : 'Editar plantilla';
      }
      return this.$t('createTemplate');
    },
    templateSamples() {
      return this.templateSamplesLocal;
    },
    templateSampleBudgetCodes() {
      return [
        ...new Set(
          this.templateSamples
            .map((sample) => this.getTemplateSampleBudgetCode(sample))
            .filter(Boolean)
        ),
      ];
    },
    hasMultipleSampleBudgets() {
      return this.templateSampleBudgetCodes.length > 1;
    },
    showCommonBudgetField() {
      return !this.hasMultipleSampleBudgets;
    },
    commonTemplateBudgetCode() {
      if (this.hasMultipleSampleBudgets) return null;
      return (
        this.templateSampleBudgetCodes[0] ||
        this.templateBudgetCode ||
        null
      );
    },
    canSaveTemplateWithCurrentBudgets() {
      return this.hasMultipleSampleBudgets
        ? this.templateSamples.every((sample) =>
            Boolean(this.getTemplateSampleBudgetCode(sample))
          )
        : true;
    },
    templateNameMaxLength() {
      return TEMPLATE_NAME_MAX_LENGTH;
    },
    templateDescriptionMaxLength() {
      return TEMPLATE_DESCRIPTION_MAX_LENGTH;
    },
    sourceRouteName() {
      return this.templateCreationDraft?.sourceRouteName || '';
    },
    showTemplateNameError() {
      return this.attemptedSubmit && !this.templateName.trim();
    },
    showTemplateNameMaxLengthError() {
      return this.templateName.length > this.templateNameMaxLength;
    },
    showTemplateDescriptionMaxLengthError() {
      return (
        this.templateDescription.length >
        this.templateDescriptionMaxLength
      );
    },
    templateNameMaxLengthErrorText() {
      return this.getMaxLengthErrorText(
        this.$t('templateName'),
        this.templateNameMaxLength
      );
    },
    templateDescriptionMaxLengthErrorText() {
      return this.getMaxLengthErrorText(
        this.$t('templateDescription'),
        this.templateDescriptionMaxLength
      );
    },
    hasTemplateTextMaxLengthError() {
      return (
        this.showTemplateNameMaxLengthError ||
        this.showTemplateDescriptionMaxLengthError
      );
    },
    statusText() {
      return this.$t('status') !== 'status'
        ? this.$t('status')
        : 'Estado';
    },
    activeText() {
      return this.$t('active') !== 'active'
        ? this.$t('active')
        : 'Activa';
    },
    inactiveText() {
      return this.$t('inactive') !== 'inactive'
        ? this.$t('inactive')
        : 'Inactiva';
    },
    budgetText() {
      return this.$t('samplesRegister.budget') !==
        'samplesRegister.budget'
        ? this.$t('samplesRegister.budget')
        : this.$t('budget') !== 'budget'
        ? this.$t('budget')
        : 'Presupuesto';
    },
    budgetOptions() {
      return Array.isArray(this.presupuestos)
        ? this.presupuestos
        : [];
    },
    normalizedBudgetOptions() {
      const budgets = Array.isArray(this.budgetOptions)
        ? [...this.budgetOptions]
        : [];
      const currentBudgetCode = this.templateBudgetCode;

      const hasCurrentBudget = budgets.some(
        (item) =>
          String(item?.CodPresupuesto || '') ===
          String(currentBudgetCode || '')
      );

      if (currentBudgetCode && !hasCurrentBudget) {
        budgets.unshift({
          CodPresupuesto: currentBudgetCode,
          CodOfferTitle: currentBudgetCode,
        });
      }

      return budgets.map((item) => ({
        ...item,
        CodPresupuesto: item?.CodPresupuesto ?? null,
        CodOfferTitle:
          item?.CodOfferTitle ||
          `${item?.CodPresupuesto || ''}${
            item?.Titulo ? ` - ${item.Titulo}` : ''
          }${
            item?.CodEstado === '1'
              ? ` (${this.$t(
                  this.$helpers.getStatusTextKey(item.Estado)
                )})`
              : ''
          }`,
      }));
    },
    templateStatusOptions() {
      return [
        {
          label: this.activeText,
          value: true,
        },
        {
          label: this.inactiveText,
          value: false,
        },
      ];
    },
  },
  beforeMount() {
    this.initializeDraft();
    this.setCurrentRoute(this.buildBreadcrumb());
  },
  mounted() {
    if (!this.templateCreationDraft) {
      this.navigateWithoutDraft();
      return;
    }
    this.loadBudgets();
    this.loaded = true;
    this.scheduleSamplesGuidedTourStart();
  },
  beforeDestroy() {
    this.clearSamplesGuidedTourStartTimeout();
    stopSamplesRegisterGuidedTour(this);
  },
  methods: {
    clearSamplesGuidedTourStartTimeout() {
      if (this.guidedTourStartTimeoutId) {
        clearTimeout(this.guidedTourStartTimeoutId);
        this.guidedTourStartTimeoutId = null;
      }
    },
    scheduleSamplesGuidedTourStart({ force = false } = {}) {
      if (!this.loaded) return;
      const shouldContinueFromSamplesRegister =
        force ||
        consumePendingSamplesRegisterTemplateCreateTourStart();
      if (!shouldContinueFromSamplesRegister) {
        return;
      }

      this.clearSamplesGuidedTourStartTimeout();
      this.guidedTourStartTimeoutId = setTimeout(() => {
        this.guidedTourStartTimeoutId = null;
        startSamplesRegisterTemplateCreateGuidedTour(this, {
          force: true,
        });
      }, SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY);
    },
    openTourTemplatesList() {
      this.$f7router.navigate(
        {
          name: 'TemplatesListPage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
      return true;
    },
    normalizeTemplateText(value) {
      return typeof value === 'string' ? value.trim() : value || '';
    },
    getMaxLengthErrorText(fieldName, maxLength) {
      return `${fieldName} ${this.$t(
        'templateMaxLengthExceeded'
      )} ${maxLength} ${this.$t('templateMaxLengthCharacters')}`;
    },
    toNullableNumber(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    },
    getTemplateBudgetCode(defaults = {}) {
      const budget =
        defaults?.codPresupuesto || defaults?.CodPresupuesto || null;
      if (!budget) return null;

      if (typeof budget === 'object') {
        return (
          budget.id ||
          budget.codigo ||
          budget.codPresupuesto ||
          budget.CodPresupuesto ||
          null
        );
      }

      return budget;
    },
    getTemplateSampleBudgetObject(sample = {}) {
      return sample?.codPresupuesto || sample?.CodPresupuesto || null;
    },
    getTemplateSampleBudgetCode(sample = {}) {
      const budget = this.getTemplateSampleBudgetObject(sample);
      if (!budget) return null;

      if (typeof budget === 'object') {
        return (
          budget.id ||
          budget.codigo ||
          budget.codPresupuesto ||
          budget.CodPresupuesto ||
          null
        );
      }

      return budget;
    },
    getTemplateSampleBudgetValue(sample = {}) {
      const budget = this.getTemplateSampleBudgetObject(sample);
      const budgetCode = this.getTemplateSampleBudgetCode(sample);
      if (!budgetCode) return null;

      if (typeof budget === 'object') {
        return {
          id: budgetCode,
          descripcion:
            this.normalizeTemplateText(
              budget.descripcion || budget.Descripcion
            ) || budgetCode,
        };
      }

      const selectedBudget =
        this.normalizedBudgetOptions.find(
          (item) =>
            String(item?.CodPresupuesto || '') ===
            String(budgetCode || '')
        ) || null;
      return {
        id: budgetCode,
        descripcion:
          selectedBudget?.CodOfferTitle ||
          selectedBudget?.Titulo ||
          budgetCode,
      };
    },
    normalizeTemplateSample(sample, index) {
      const normalizedSample = {
        ...sample,
        order: sample?.order || index + 1,
        descripcionMuestra: this.normalizeTemplateText(
          sample?.descripcionMuestra ?? sample?.DescripcionMuestra
        ),
        observaciones: this.normalizeTemplateText(
          sample?.observaciones ?? sample?.Observaciones
        ),
      };

      delete normalizedSample.CodPresupuesto;

      const sampleBudgetValue =
        this.getTemplateSampleBudgetValue(sample);
      if (sampleBudgetValue) {
        normalizedSample.codPresupuesto = sampleBudgetValue;
      } else {
        delete normalizedSample.codPresupuesto;
        delete normalizedSample.CodPresupuesto;
      }

      return normalizedSample;
    },
    initializeDraft() {
      this.templateName =
        this.templateCreationDraft?.template?.name || '';
      this.templateDescription =
        this.templateCreationDraft?.template?.shortDescription || '';
      this.templateActive = Boolean(
        this.templateCreationDraft?.template?.active ?? true
      );
      this.templateSamplesLocal = Array.isArray(
        this.templateCreationDraft?.samples
      )
        ? this.templateCreationDraft.samples.map((sample) => ({
            ...sample,
          }))
        : [];
      this.templateBudgetCode =
        this.getTemplateBudgetCode(
          this.templateCreationDraft?.defaults
        ) ||
        this.getTemplateSampleBudgetCode(
          this.templateSamplesLocal[0]
        ) ||
        null;
    },
    buildBreadcrumb() {
      const current = {
        name: this.pageTitleText,
        disabled: false,
        page: 'TemplateCreatePage',
        active: true,
      };

      if (this.sourceRouteName === 'SamplesRegister') {
        return [
          {
            name: this.$t('SamplesRegister'),
            disabled: false,
            page: 'SamplesRegister',
            active: false,
          },
          current,
        ];
      }

      return [
        {
          name:
            this.$t('TemplatesListTitle') !== 'TemplatesListTitle'
              ? this.$t('TemplatesListTitle')
              : 'Plantillas de muestreo',
          disabled: false,
          page: 'TemplatesListPage',
          active: false,
        },
        current,
      ];
    },
    navigateWithoutDraft() {
      this.$f7router.navigate(
        {
          name:
            this.sourceRouteName === 'SamplesRegister'
              ? 'SamplesRegister'
              : 'TemplatesListPage',
        },
        {
          animate: false,
        }
      );
    },
    buildPayload() {
      const shortDescription = this.templateDescription.trim();
      const defaults = {
        ...(this.templateCreationDraft?.defaults || {}),
      };
      const budgetDefault = this.hasMultipleSampleBudgets
        ? null
        : this.buildBudgetDefaultPayload(
            this.commonTemplateBudgetCode
          );

      if (budgetDefault) {
        delete defaults.CodPresupuesto;
        defaults.codPresupuesto = budgetDefault;
      } else {
        delete defaults.codPresupuesto;
        delete defaults.CodPresupuesto;
      }

      const payload = {
        ...(this.templateCreationDraft || {}),
        samples: this.templateSamples.map((sample, index) =>
          this.normalizeTemplateSample(sample, index)
        ),
        template: {
          name: this.templateName.trim(),
          active: Boolean(this.templateActive),
          ...(shortDescription ? { shortDescription } : {}),
        },
        ...(Object.keys(defaults).length ? { defaults } : {}),
      };

      delete payload.sourceRouteName;
      delete payload.editingTemplateId;
      return payload;
    },
    getSelectedBudgetOption() {
      const normalizedBudgetCode =
        this.normalizeTemplateText(this.templateBudgetCode) || null;
      if (!normalizedBudgetCode) return null;

      return (this.normalizedBudgetOptions || []).find(
        (item) =>
          String(item?.CodPresupuesto || '') ===
          String(normalizedBudgetCode)
      );
    },
    buildBudgetDefaultPayload(budgetCode = this.templateBudgetCode) {
      const normalizedBudgetCode =
        this.normalizeTemplateText(budgetCode) || null;
      if (!normalizedBudgetCode) return null;

      const selectedBudget = this.getSelectedBudgetOption();
      const budgetDescription =
        selectedBudget?.Titulo ||
        selectedBudget?.CodOfferTitle ||
        normalizedBudgetCode;

      return {
        id: normalizedBudgetCode,
        descripcion:
          this.normalizeTemplateText(budgetDescription) || null,
      };
    },
    hasRequiredSampleFields(sample = {}) {
      const familyId =
        sample?.familiaTM?.id ??
        sample?.idFamilia ??
        sample?.IdFamilia ??
        sample?.FamiliaTM ??
        sample?.familiaTMId ??
        null;
      const sampleTypeId =
        sample?.tipoMuestra?.id ??
        sample?.idTipoMuestra ??
        sample?.IdTipoMuestra ??
        sample?.Descripcion?.idTipoMuestra ??
        sample?.Descripcion ??
        sample?.tipoMuestraId ??
        null;
      const articleId =
        sample?.articulo?.id ??
        sample?.CodArticulo ??
        sample?.codArticulo ??
        sample?.Articulo ??
        sample?.articuloId ??
        null;

      return Boolean(familyId && sampleTypeId && articleId);
    },
    getBackRouteName() {
      return (
        this.$f7router?.previousRoute?.name ||
        this.sourceRouteName ||
        'TemplatesListPage'
      );
    },
    getSamplesRegisterReturnRoute() {
      const sourceRegisterMode =
        this.templateCreationDraft?.sourceRegisterMode || '';
      const sourceRegisterTab =
        this.templateCreationDraft?.sourceRegisterTab;

      return {
        name: 'SamplesRegister',
        query: {
          ...(sourceRegisterMode === 'express'
            ? { mode: 'express' }
            : {}),
          tab:
            Number.isInteger(sourceRegisterTab) &&
            sourceRegisterTab >= 0
              ? sourceRegisterTab
              : 0,
        },
      };
    },
    goBack() {
      const sourceRouteName = this.sourceRouteName;
      const backRoute =
        sourceRouteName === 'SamplesRegister'
          ? this.getSamplesRegisterReturnRoute()
          : {
              name: this.getBackRouteName(),
            };
      this.setTemplateCreationDraft(null);
      this.$f7router.navigate(
        backRoute,
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    onTemplateNameChanged(e) {
      this.templateName = e?.value || '';
    },
    onTemplateDescriptionChanged(e) {
      this.templateDescription = e?.value ?? e?.target?.value ?? '';
    },
    onTemplateActiveChanged(e) {
      this.templateActive = Boolean(e?.value);
    },
    async onTemplateBudgetChanged(e) {
      const previousBudgetCode = this.templateBudgetCode;
      const nextBudgetCode = e?.value || null;
      this.templateBudgetCode = nextBudgetCode;

      if (
        String(previousBudgetCode || '') ===
        String(nextBudgetCode || '')
      ) {
        return;
      }

      try {
        this.$f7.preloader.show();
        await this.reconcileSamplesForBudgetChange(nextBudgetCode);
        await this.applyBudgetChangeToTemplateSamplesGrid();
      } finally {
        this.$f7.preloader.hide();
      }
    },
    onTemplateSamplesUpdated(samples = []) {
      this.templateSamplesLocal = Array.isArray(samples)
        ? samples.map((sample) => ({
            ...sample,
          }))
        : [];
    },
    getSampleFamilyId(sample = {}) {
      return (
        sample?.familiaTM?.id ??
        sample?.familiaTM?.idFamiliaTM ??
        sample?.FamiliaTM?.id ??
        sample?.FamiliaTM?.idFamiliaTM ??
        sample?.idFamilia ??
        sample?.IdFamilia ??
        sample?.FamiliaTM ??
        sample?.familiaTMId ??
        null
      );
    },
    async reconcileSamplesForBudgetChange(budgetCode) {
      if (!budgetCode) {
        this.templateSamplesLocal = this.templateSamples.map(
          (sample) =>
            this.clearSampleSelectionsForBudgetChange(sample)
        );
        return;
      }

      try {
        await this.fetchDetallesPresupuestos(budgetCode);
        const availableFamilyIds = new Set(
          (this.detallesPresupuestos || [])
            .map((line) => this.toNullableNumber(line?.IdFamilia))
            .filter((familyId) => familyId !== null)
        );

        if (!availableFamilyIds.size) {
          this.templateSamplesLocal = this.templateSamples.map(
            (sample) =>
              this.clearSampleSelectionsForBudgetChange(sample)
          );
          return;
        }

        this.templateSamplesLocal = this.templateSamples.map(
          (sample) => {
            const familyId = this.toNullableNumber(
              this.getSampleFamilyId(sample)
            );
            return familyId !== null &&
              availableFamilyIds.has(familyId)
              ? {
                  ...sample,
                }
              : this.clearSampleSelectionsForBudgetChange(sample);
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    async applyBudgetChangeToTemplateSamplesGrid() {
      await this.$nextTick();
      await this.$refs.templateSamplesDetails?.reconcileRowsForBudgetChange?.();
    },
    clearSampleSelectionsForBudgetChange(sample = {}) {
      return {
        ...sample,
        familiaTM: null,
        idFamilia: null,
        IdFamilia: null,
        FamiliaTM: null,
        familiaTMId: null,
        familiaTMDescripcion: null,
        tipoMuestra: null,
        idTipoMuestra: null,
        IdTipoMuestra: null,
        Descripcion: null,
        tipoMuestraId: null,
        tipoMuestraDescripcion: null,
        subTipoMuestra: null,
        idSubTipoMuestra: null,
        IdSubTipoMuestra: null,
        SubTipoMuestra: null,
        subTipoMuestraId: null,
        subTipoMuestraDescripcion: null,
        articulo: null,
        Articulo: null,
        CodArticulo: null,
        codArticulo: null,
        articuloId: null,
        articuloDescripcion: null,
        legislacion: null,
        idLegislacion: null,
        IdLegislacion: null,
        Legislacion: null,
        legislacionId: null,
        legislacionDescripcion: null,
        cma: false,
        lmr: false,
        trazaCultivo: false,
        cultivoOrganico: false,
        CodLinea: null,
        idSubFamiliaTM: null,
        IdSubFamiliaTM: null,
        PrecioAT: null,
        Descuento: null,
      };
    },
    getBudgetOptionText(option) {
      return option?.CodOfferTitle || option?.CodPresupuesto || '';
    },
    async loadBudgets() {
      try {
        await this.fetchPresupuestos();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    async saveTemplate() {
      this.attemptedSubmit = true;
      if (!this.templateName.trim()) return;
      if (this.hasTemplateTextMaxLengthError) return;
      if (!this.templateSamples.length) return;
      if (!this.templateSamples.every(this.hasRequiredSampleFields)) {
        this.$notifyDX(
          {
            message: this.$t('samplesRegister.missingFieldsError'),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return;
      }

      try {
        this.savingTemplate = true;
        this.$f7.preloader.show();

        const payload = this.buildPayload();
        let savedTemplate = null;

        if (this.isEditMode) {
          savedTemplate = await this.updateTemplate({
            id: this.editingTemplateId,
            data: payload,
          });
          await this.fetchTemplate(this.editingTemplateId);
        } else {
          savedTemplate = await this.createTemplate(payload);
        }

        emitSamplesRegisterGa4Event(
          this,
          'sampling_template_save_success',
          {
            screen_name: 'TemplateCreatePage',
            entry_point: this.isEditMode
              ? 'template_details_edit_button'
              : 'express_sample_register_save_template_button',
            template_mode: this.isEditMode ? 'edit' : 'create',
            template_id:
              savedTemplate?.template?.id ||
              savedTemplate?.id ||
              this.editingTemplateId ||
              undefined,
            samples_count: payload.samples.length,
            budget_code:
              this.normalizeTemplateText(this.templateBudgetCode) ||
              undefined,
            template_active: Boolean(this.templateActive),
          }
        );

        const sourceRouteName = this.sourceRouteName;

        this.$notifyDX(
          {
            message: this.isEditMode
              ? this.$t('templateUpdatedSuccess') !==
                'templateUpdatedSuccess'
                ? this.$t('templateUpdatedSuccess')
                : 'La plantilla se ha actualizado correctamente'
              : this.$t('templateSavedSuccess') !==
                'templateSavedSuccess'
              ? this.$t('templateSavedSuccess')
              : 'La plantilla se ha guardado correctamente',
            width: 550,
          },
          'success',
          NOTIFY_TIME_LONG
        );

        const successRoute = this.isEditMode
          ? sourceRouteName === 'TemplateDetailsPage'
            ? {
                name: 'TemplateDetailsPage',
              }
            : sourceRouteName === 'SamplesRegister'
            ? this.getSamplesRegisterReturnRoute()
            : {
                name: 'TemplatesListPage',
              }
          : sourceRouteName === 'SamplesRegister'
          ? this.getSamplesRegisterReturnRoute()
          : {
              name: 'TemplatesListPage',
            };

        this.setTemplateCreationDraft(null);
        this.$f7router.navigate(successRoute, {
          animate: true,
          transition: 'f7-fade',
        });
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.savingTemplate = false;
        this.$f7.preloader.hide();
      }
    },
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('samplingTemplatesModule', [
      'createTemplate',
      'updateTemplate',
      'fetchTemplate',
      'setTemplateCreationDraft',
    ]),
    ...mapActions('Offers', [
      'fetchPresupuestos',
      'fetchDetallesPresupuestos',
    ]),
  },
  watch: {
    loaded(value) {
      if (value) {
        this.scheduleSamplesGuidedTourStart();
      }
    },
  },
};
</script>

<style scoped>
.template-create-page {
  display: flex;
  flex-direction: column;
}

.template-create-page > * + * {
  margin-top: 18px;
}

.template-create-page__form {
  width: 100%;
}

.template-create-page__form :deep(.dx-layout-manager),
.template-create-page__form :deep(.dx-field-item),
.template-create-page__form :deep(.dx-field-item-content),
.template-create-page__form :deep(.dx-texteditor),
.template-create-page__form :deep(.dx-textarea) {
  width: 100%;
}

.template-create-page__intro,
.template-create-page__meta {
  color: var(--Neutral-70, #3e3c44);
  font-size: 14px;
  line-height: 140%;
}

.template-create-page__label {
  display: block;
  margin-bottom: 8px;
  color: #04589f;
  font-size: 14px;
  font-weight: 600;
}

/* .template-create-page__textbox {
  width: 100%;
} */

.template-create-page__error {
  margin-top: 8px;
  color: #a80d0b;
  font-size: 12px;
}
.template-create-page__preview-title {
  color: #04589f;
  font-size: 16px;
  font-weight: 600;
}

.template-create-page__preview .custom-template-samples {
  margin-top: 0;
}

.template-create-page__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.template-create-page__actions > * + * {
  margin-left: 12px;
}

.template-create-page__actions .btn-accept {
  color: white;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 1rem;
}

.template-create-page__actions .btn-cancel {
  color: #6c757d;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 1rem;
  border: 1px solid #6c757d;
}

.template-create-page__form-grid {
  display: grid;
  grid-template-columns:
    minmax(0, 1.2fr)
    minmax(0, 1.2fr)
    minmax(0, 0.8fr)
    minmax(0, 1fr);
  gap: 32px;
  width: 100%;
  align-items: start;
}

.template-create-page__field {
  width: 100%;
}

.template-create-description-field {
  grid-column: span 2;
}
.template-create-page__field--budget {
  grid-column: span 1;
}

.template-create-page__label {
  display: block;
  margin-bottom: 8px;
  color: #04589f;
  font-size: 14px;
  font-weight: 600;
}

.template-create-page__field :deep(.dx-texteditor),
.template-create-page__field :deep(.dx-textarea) {
  width: 100% !important;
}

@media (max-width: 768px) {
  .template-create-page__form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .template-create-page__field--budget {
    grid-column: auto;
  }

  .template-create-page__actions {
    flex-direction: column-reverse;
  }

  .template-create-page__actions > * + * {
    margin-left: 0;
    margin-bottom: 12px;
  }
}

@media (max-width: 768px) {
  .template-create-page__actions {
    flex-direction: column-reverse;
  }

  .template-create-page__actions > * + * {
    margin-left: 0;
    margin-bottom: 12px;
  }
}
</style>
